import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import bemify from 'utils/bemify';

// Components
import Icon from 'components/icon';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);


// Utils
const clamp = (value, min, max) => {
  const lowerBound = typeof min === 'number' ? min : -Infinity;
  const upperBound = typeof max === 'number' ? max : Infinity;

  return Math.max(lowerBound, Math.min(upperBound, value));
};

const QuantitySelector = ({
  ariaLabel = 'Quantity',
  className,
  defaultValue = '',
  disabled = false,
  id,
  max,
  min = 1,
  onChange,
  step = 1,
  value = ''
}) => {
  // States
  const [currentValue, setCurrentValue] = useState(defaultValue);

  // Methods
  const setValue = useCallback(
    (newValue) => {
      const clamped = clamp(newValue, min, max);

      if (clamped !== currentValue) {
        setCurrentValue(clamped);

        onChange(clamped);
      }
    },
    [currentValue, min, max, onChange]
  );

  const handleInputBlur = useCallback((event) => {
    setValue(event.target.value);
  }, [setValue]);

  const handleInputChange = useCallback((event) => {
    setCurrentValue(event.target.value);
  }, [setCurrentValue]);

  const increment = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled) return;
    setValue((currentValue ?? min) + step);
  }, [disabled, setValue, currentValue, step, min]);

  const decrement = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled) return;
    setValue((currentValue ?? min) - step);
  }, [disabled, setValue, currentValue, step, min]);

  const onKeyDown = useCallback(
    (event) => {
      if (disabled) return;
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowRight':
          event.preventDefault();
          setValue((currentValue ?? min) + step);
          break;
        case 'ArrowDown':
        case 'ArrowLeft':
          event.preventDefault();
          setValue((currentValue ?? min) - step);
          break;
        case 'PageUp':
          event.preventDefault();
          setValue((currentValue ?? min) + (step * 10));
          break;
        case 'PageDown':
          event.preventDefault();
          setValue((currentValue ?? min) - (step * 10));
          break;
        case 'Home':
          if (typeof min === 'number') {
            event.preventDefault();
            setValue(min);
          }
          break;
        case 'End':
          if (typeof max === 'number') {
            event.preventDefault();
            setValue(max);
          }
          break;
        default:
          break;
      }
    },
    [disabled, setValue, currentValue, step, min, max]
  );

  // Computed
  const canDecrement = !disabled && typeof min === 'number' ? currentValue > min : !disabled;
  const canIncrement = !disabled && typeof max === 'number' ? currentValue < max : !disabled;

  // Effects
  useEffect(() => {
    const clamped = clamp(value, min, max);

    setCurrentValue(clamped);

    if (clamped !== value) {
      onChange(clamped);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max]);

  return (
    <label
      className={`${styles.quantitySelector}${className ? ` ${className}` : ''}`}
      htmlFor={id}
      onClick={event => event.stopPropagation()}
    >
      <button
        aria-label="Decrease quantity"
        className={styles.quantitySelector__button}
        disabled={!canDecrement}
        type="button"
        onClick={decrement}
      >
        <Icon
          className={styles.quantitySelector__icon}
          name="minus"
        />
      </button>
      <input
        aria-label={ariaLabel}
        aria-valuemax={typeof max === 'number' ? max : null}
        aria-valuemin={typeof min === 'number' ? min : null}
        className={styles.quantitySelector__input}
        id={id}
        tabIndex={disabled ? -1 : 0}
        type="number"
        value={currentValue}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onKeyDown={onKeyDown}
      />
      <button
        aria-label="Increase quantity"
        className={styles.quantitySelector__button}
        disabled={!canIncrement}
        type="button"
        onClick={increment}
      >
        <Icon
          className={styles.quantitySelector__icon}
          name="plus"
        />
      </button>
    </label>
  );
};


QuantitySelector.propTypes = {
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  defaultValue: PropTypes.number,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
  step: PropTypes.number,
  value: PropTypes.number
};


export default QuantitySelector;
